<template>
  <v-autocomplete
    placeholder="Select a warehouse"
    variant="outlined"
    data-testid="yard-warehouse-select"
    color="primary"
    density="compact"
    item-title="name"
    item-value="id"
    clear-on-select
    return-object
    hide-details
    :model-value="modelValue"
    @update:model-value="value => $emit('update:model-value', value)"
    :items="warehouses"
    :loading="isLoading" />
</template>

<script>
import { defineComponent, onBeforeMount, onMounted, onUnmounted, ref } from 'vue';
import warehouseService from '@satellite/services/warehouse-service';
import { useEventHub } from '@/composables';

export default defineComponent({
  name: 'YardViewWarehouseSelect',
  props: {
    modelValue: {
      type: [Object, null],
      required: true
    }
  },
  emits: ['update:model-value', 'update:items'],
  setup(props, { emit }) {
    const isLoading = ref(false);
    const warehouses = ref([]);

    const eventHub = useEventHub();

    const setWarehouses = async () => {
      isLoading.value = true;
      try {
        const response = await warehouseService.getWarehouses({
          select: 'id,name,timezone'
        });

        warehouses.value = (response ?? []).sort((a, b) => a.name.localeCompare(b.name));
        emit('update:items', warehouses.value);

        if (props.modelValue && !warehouses.value.some(wh => wh.id === props.modelValue.id)) {
          // If the current model value is not in the list, reset it to null
          emit('update:model-value', null);
        }
      } finally {
        isLoading.value = false;
      }
    };

    const handleUpdateWarehouseEvent = warehouse => {
      if (warehouse?.id) {
        const index = warehouses.value.findIndex(wh => wh.id === warehouse.id);

        if (index >= 0) {
          const updatedWarehouse = {
            ...warehouses.value[index],
            ...warehouse
          };
          warehouses.value.splice(index, 1, updatedWarehouse);

          emit('update:items', warehouses.value);
        }
      }
    };

    onBeforeMount(setWarehouses);

    onMounted(() => {
      eventHub.on('delete-Warehouse', setWarehouses);
      eventHub.on('create-Warehouse', setWarehouses);
      eventHub.on('update-Warehouse', handleUpdateWarehouseEvent);
    });

    onUnmounted(() => {
      eventHub.off('delete-Warehouse', setWarehouses);
      eventHub.off('create-Warehouse', setWarehouses);
      eventHub.off('update-Warehouse', handleUpdateWarehouseEvent);
    });

    return {
      isLoading,
      warehouses
    };
  }
});
</script>
